import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import {
    Store,
    Package,
    Users,
    TrendingUp,
    ShoppingCart,
    DollarSign,
    AlertTriangle,
    Activity,
} from "lucide-react";
import IOTRefri from "./IOTRefri";

const DashboardOverview = () => {
    const [stats, setStats] = useState({
        totalStores: 0,
        totalProducts: 0,
        totalUsers: 0,
        lowStockCount: 0,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchDashboardStats();
    }, []);

    const fetchDashboardStats = async () => {
        try {
            // Obtener conteo de sucursales
            const storesSnap = await getDocs(collection(db, "stores"));
            const totalStores = storesSnap.size;

            // Obtener conteo de productos
            const productsSnap = await getDocs(collection(db, "products"));
            const totalProducts = productsSnap.size;

            // Obtener conteo de usuarios
            const usersSnap = await getDocs(collection(db, "Users"));
            const totalUsers = usersSnap.size;

            // Obtener productos con bajo stock (simulado por ahora)
            const lowStockCount = 5;

            setStats({
                totalStores,
                totalProducts,
                totalUsers,
                lowStockCount,
            });
        } catch (error) {
            console.error("Error fetching dashboard stats:", error);
        } finally {
            setLoading(false);
        }
    };

    const statCards = [
        {
            title: "Total Sucursales",
            value: stats.totalStores,
            icon: Store,
            color: "blue",
            bgColor: "bg-blue-50",
            iconColor: "text-blue-600",
        },
        {
            title: "Total Productos",
            value: stats.totalProducts,
            icon: Package,
            color: "purple",
            bgColor: "bg-purple-50",
            iconColor: "text-purple-600",
        },
        {
            title: "Usuarios Registrados",
            value: stats.totalUsers,
            icon: Users,
            color: "green",
            bgColor: "bg-green-50",
            iconColor: "text-green-600",
        },
        {
            title: "Stock Bajo",
            value: stats.lowStockCount,
            icon: AlertTriangle,
            color: "orange",
            bgColor: "bg-orange-50",
            iconColor: "text-orange-600",
        },
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-[#FF0000] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">Cargando estadísticas...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-[#FF0000] to-[#CC0000] rounded-2xl p-6 md:p-8 text-white shadow-lg">
                <div className="flex items-center gap-4">
                    <div className="bg-white/20 p-4 rounded-2xl">
                        <Activity className="w-8 h-8" strokeWidth={2} />
                    </div>
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold mb-2">
                            Panel de Control
                        </h2>
                        <p className="text-white/90">
                            Gestiona tus sucursales, productos, inventario y usuarios desde aquí
                        </p>
                    </div>
                </div>
            </div>

            {/* IoT Panel - Refrigeración */}
            <IOTRefri />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {statCards.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                        <div
                            key={index}
                            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className={`${stat.bgColor} p-3 rounded-xl inline-flex mb-4`}>
                                        <Icon className={`h-6 w-6 ${stat.iconColor}`} strokeWidth={2} />
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 mb-1">
                                        {stat.value}
                                    </h3>
                                    <p className="text-sm text-gray-500">{stat.title}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Recent Activity */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Actividad Reciente</h3>
                        <Activity className="h-5 w-5 text-gray-400" strokeWidth={2} />
                    </div>
                    <div className="space-y-3">
                        {[
                            { action: "Nueva sucursal añadida", time: "Hace 2 horas" },
                            { action: "Producto actualizado", time: "Hace 5 horas" },
                            { action: "Usuario registrado", time: "Hace 1 día" },
                        ].map((item, index) => (
                            <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                                <p className="text-sm text-gray-700">{item.action}</p>
                                <p className="text-xs text-gray-500">{item.time}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Estadísticas Rápidas</h3>
                        <TrendingUp className="h-5 w-5 text-green-500" strokeWidth={2} />
                    </div>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Productos Activos</span>
                            <span className="text-lg font-bold text-gray-900">{stats.totalProducts}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Sucursales Operando</span>
                            <span className="text-lg font-bold text-gray-900">{stats.totalStores}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Usuarios Activos</span>
                            <span className="text-lg font-bold text-gray-900">{stats.totalUsers}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* System Status */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Sistema</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">Base de Datos: Operacional</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">Autenticación: Activa</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-gray-700">Storage: Disponible</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardOverview;

